import React, { useEffect, useState } from "react";
import { gapi } from "gapi-script";


const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const SCOPES = "https://www.googleapis.com/auth/drive.readonly";

const GoogleDriveExplorer = () => {
    const [files, setFiles] = useState([]);
    const [isSignedIn, setIsSignedIn] = useState(false);

    useEffect(() => {
        function start() {
            gapi.load("client:auth2", () => {
                gapi.client.init({
                    apiKey: API_KEY,
                    clientId: CLIENT_ID,
                    scope: SCOPES,
                    discoveryDocs: ["https://www.googleapis.com/discovery/v1/apis/drive/v3/rest"],
                }).then(() => {
                    if (gapi.auth2.getAuthInstance()) {
                        const authInstance = gapi.auth2.getAuthInstance();
                        setIsSignedIn(authInstance.isSignedIn.get());
                        authInstance.isSignedIn.listen(setIsSignedIn);
                    }
                }).catch(error => console.error("Error initializing gapi:", error));
            });
        }
        start();
    }, []);

    const signIn = () => {
        if (gapi.auth2 && gapi.auth2.getAuthInstance()) {
            gapi.auth2.getAuthInstance().signIn();
        } else {
            console.error("gapi.auth2 is not initialized yet.");
        }
    };

    const signOut = () => {
        if (gapi.auth2 && gapi.auth2.getAuthInstance()) {
            gapi.auth2.getAuthInstance().signOut();
        }
    };

    const findAIATFolder = async () => {
        try {

            // Search for the "AIAT" folder in the root
            const response = await gapi.client.drive.files.list({
                q: "name = 'AIAT' and mimeType = 'application/vnd.google-apps.folder'",
                fields: "files(id, name)",
                pageSize: 1, // We only need one folder
            });

            const folders = response.result.files;
            if (folders.length === 0) {
                console.error("AIAT folder not found.");
                return null;
            }

            return folders[0].id; // Return the folder ID
        } catch (error) {
            console.error("Error finding AIAT folder:", error);
            return null;
        }
    };


    const fetchFolderContents = async (folderId) => {
        try {
            setLoading(true)
            const response = await gapi.client.drive.files.list({
                q: `'${folderId}' in parents`,
                fields: "files(id, name, mimeType, parents)",
                pageSize: 100,
            });

            const items = response.result.files || [];
            let folderStructure = [];

            for (const item of items) {
                if (item.mimeType === "application/vnd.google-apps.folder") {
                    // Recursively fetch subfolders
                    const subfolderContents = await fetchFolderContents(item.id);
                    folderStructure.push({
                        ...item,
                        children: subfolderContents, // Store subfolder contents inside children
                    });
                } else {
                    // Push regular files
                    folderStructure.push(item);
                }
            }

            return folderStructure;
        } catch (error) {
            console.error("Error fetching folder contents:", error);
            return [];
        } finally { setLoading(false) }
    };


    const [loading, setLoading] = useState(false);

    const listDriveFiles = async () => {
        try {
            const aiatFolderId = await findAIATFolder();

            if (!aiatFolderId) {
                console.error("No AIAT folder found.");
                return;
            }

            const aiatContents = await fetchFolderContents(aiatFolderId);

            setFiles(aiatContents); // Set the full file structure
        } catch (error) {
            console.error("Error fetching AIAT files:", error);
            setFiles([]);
        }
    };


    const LoadingSpinner = () => {
        return (
            <div className="flex flex-col items-center justify-center space-y-2">
                <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-500 text-sm">Loading files...</p>
            </div>
        );
    };





    const buildFolderTree = (files) => {
        let tree = {};
        files.forEach((file) => {
            if (file.mimeType === "application/vnd.google-apps.folder") {
                tree[file.id] = { ...file, children: [] };
            }
        });
        files.forEach((file) => {
            if (file.parents && tree[file.parents[0]]) {
                tree[file.parents[0]].children.push(file);
            }
        });
        return Object.values(tree);
    };

    return (
        <div className="p-8 max-w-[90vw] min-h-[90vh] mx-auto bg-white shadow-lg rounded-xl border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
                <i className="fas fa-folder-open text-blue-600"></i>
                <span>File Explorer</span>
            </h2>

            {isSignedIn ? (
                <>
                    <div className="flex items-center space-x-4 mb-6">
                        <button
                            onClick={signOut}
                            className="bg-red-500 text-white px-5 py-2 rounded-lg shadow-md hover:bg-red-600 transition duration-200"
                        >
                            <i className="fas fa-sign-out-alt mr-2"></i> Sign Out
                        </button>

                        <button
                            onClick={listDriveFiles}
                            className="bg-blue-500 text-white px-5 py-2 rounded-lg shadow-md hover:bg-blue-600 transition duration-200"
                        >
                            <i className="fas fa-sync-alt mr-2"></i> Load Files
                        </button>
                        <div className="bg-white p-4 rounded-lg shadow-inner max-h-[400px] overflow-y-auto">
                            {loading && (<LoadingSpinner />)}
                        </div>
                    </div>

                    <div className="bg-gray-100 p-4 rounded-lg shadow-inner max-h-[70vh] overflow-y-auto">
                        <FileExplorer files={files} />
                    </div>
                </>
            ) : (
                <div className="flex flex-col items-center">
                    <button
                        onClick={signIn}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg shadow-md hover:bg-blue-700 transition duration-200 flex items-center space-x-2"
                    >
                        <i className="fab fa-google"></i>
                        <span>Sign in with Google</span>
                    </button>
                    <p className="text-gray-500 mt-2 text-sm">Access your Google Drive files</p>

                </div>
            )}

        </div>

    );
};


const FileExplorer = ({ files = [] }) => {
    if (!files || !Array.isArray(files)) {
        return <p className="text-gray-500 text-center">No files found.</p>;
    }

    return (
        <ul className="space-y-3">
            {files.map((file) => (
                <li key={file.id} className="group">
                    {file.mimeType === "application/vnd.google-apps.folder" ? (
                        <details className="cursor-pointer">
                            <summary
                                className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition duration-200"
                            >
                                <i className="fas fa-folder text-blue-600"></i>
                                <span className="font-medium">{file.name}</span>
                            </summary>
                            <div className="ml-6 mt-2 border-l border-gray-300 pl-4">
                                {<FileExplorer files={file.children} />}
                            </div>
                        </details>
                    ) : (
                        <div
                            className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200 transition duration-200"
                        >
                            {getFileIcon(file.mimeType)}
                            <a
                                href={`https://drive.google.com/uc?id=${file.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-blue-600 transition duration-200 font-medium"
                            >
                                {file.name}
                            </a>
                            <a
                                href={`https://drive.google.com/uc?id=${file.id}&export=download`}
                                download
                                className="ml-auto text-green-600 hover:text-green-800 transition duration-200"
                            >
                                ⬇️
                            </a>
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );
};

// Helper function to return file icons
const getFileIcon = (mimeType) => {
    if (mimeType.startsWith("image/")) return <i className="fas fa-file-image text-yellow-500"></i>;
    if (mimeType.startsWith("video/")) return <i className="fas fa-file-video text-purple-500"></i>;
    if (mimeType.startsWith("audio/")) return <i className="fas fa-file-audio text-green-500"></i>;
    if (mimeType === "application/pdf") return <i className="fas fa-file-pdf text-red-500"></i>;
    if (mimeType.includes("spreadsheet")) return <i className="fas fa-file-excel text-green-600"></i>;
    if (mimeType.includes("presentation")) return <i className="fas fa-file-powerpoint text-orange-500"></i>;
    if (mimeType.includes("word")) return <i className="fas fa-file-word text-blue-600"></i>;
    return <i className="fas fa-file-alt text-gray-500"></i>;
};


export default GoogleDriveExplorer;
